/**
 * EmptyState Component
 *
 * Displays empty state messages with call-to-action buttons.
 * Used when dashboard has no documents or folder is empty.
 *
 * @module features/dashboard/components/EmptyState
 * @see SPEC-016 Requirements 9.2, 9.3
 * @see SPEC-017 Task 7.4 - i18n integration
 */

import React, { useMemo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/cn';
import { useTypedTranslation } from '../../../i18n';

// ============================================================================
// Variants
// ============================================================================

/**
 * EmptyState variants using CVA
 */
const emptyStateVariants = cva(
  ['flex flex-col items-center justify-center', 'text-center', 'py-12 px-6'],
  {
    variants: {
      size: {
        sm: 'py-8 px-4',
        md: 'py-12 px-6',
        lg: 'py-16 px-8',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

/** Exported type for EmptyState variants */
export type EmptyStateVariants = VariantProps<typeof emptyStateVariants>;

// ============================================================================
// Types
// ============================================================================

/**
 * Empty state type determines the icon and default message
 */
export type EmptyStateType = 'no-documents' | 'empty-folder' | 'no-results' | 'error';

export interface EmptyStateProps extends EmptyStateVariants {
  /** Type of empty state */
  type: EmptyStateType;
  /** Custom title (overrides default) */
  title?: string;
  /** Custom description (overrides default) */
  description?: string;
  /** Primary action button text */
  actionLabel?: string;
  /** Primary action handler */
  onAction?: () => void;
  /** Secondary action button text */
  secondaryActionLabel?: string;
  /** Secondary action handler */
  onSecondaryAction?: () => void;
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Icons
// ============================================================================

const icons: Record<EmptyStateType, React.ReactNode> = {
  'no-documents': (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  ),
  'empty-folder': (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
      />
    </svg>
  ),
  'no-results': (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  ),
  error: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  ),
};

// ============================================================================
// Component
// ============================================================================

/**
 * EmptyState Component
 *
 * Features:
 * - Multiple empty state types with appropriate icons
 * - Customizable title and description
 * - Primary and secondary action buttons
 * - Responsive sizing
 *
 * @example
 * ```tsx
 * // No documents state with CTA
 * <EmptyState
 *   type="no-documents"
 *   actionLabel="Create Document"
 *   onAction={() => createNewDocument()}
 * />
 *
 * // Empty folder state
 * <EmptyState
 *   type="empty-folder"
 *   title="Projects folder is empty"
 * />
 *
 * // Error state with retry
 * <EmptyState
 *   type="error"
 *   actionLabel="Retry"
 *   onAction={() => refetch()}
 * />
 * ```
 *
 * @see Requirements 9.2, 9.3
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  type,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  size,
  className,
}) => {
  const { t } = useTypedTranslation();

  // Get translated content based on type
  const translatedContent = useMemo(() => {
    const typeToKey: Record<EmptyStateType, string> = {
      'no-documents': 'noDocuments',
      'empty-folder': 'emptyFolder',
      'no-results': 'noResults',
      error: 'error',
    };
    const key = typeToKey[type];
    return {
      title: t(`dashboard.emptyState.${key}.title` as Parameters<typeof t>[0]),
      description: t(`dashboard.emptyState.${key}.message` as Parameters<typeof t>[0]),
    };
  }, [t, type]);

  const displayTitle = title || translatedContent.title;
  const displayDescription = description || translatedContent.description;

  return (
    <div
      className={cn(emptyStateVariants({ size }), className)}
      role="status"
      aria-label={displayTitle}
    >
      {/* Icon */}
      <div className={cn('text-linkode-text/30 mb-4', type === 'error' && 'text-linkode-red/50')}>
        {icons[type]}
      </div>

      {/* Title */}
      <h3 className="font-mono font-bold text-lg text-linkode-text mb-2">{displayTitle}</h3>

      {/* Description */}
      <p className="text-linkode-text/60 max-w-md mb-6">{displayDescription}</p>

      {/* Actions */}
      {(actionLabel || secondaryActionLabel) && (
        <div className="flex items-center gap-3">
          {actionLabel && onAction && (
            <button
              type="button"
              onClick={onAction}
              className={cn(
                'inline-flex items-center justify-center gap-2',
                'px-6 py-3',
                'border-2 border-linkode-text rounded-linkode',
                'bg-linkode-primary text-linkode-text',
                'font-mono font-bold uppercase text-sm',
                'transition-all duration-200',
                'hover:shadow-linkode-hover hover:translate-x-0.5 hover:-translate-y-0.5',
                'focus:outline-none focus:ring-2 focus:ring-linkode-teal'
              )}
            >
              {type === 'no-documents' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              )}
              {actionLabel}
            </button>
          )}

          {secondaryActionLabel && onSecondaryAction && (
            <button
              type="button"
              onClick={onSecondaryAction}
              className={cn(
                'inline-flex items-center justify-center gap-2',
                'px-6 py-3',
                'border-2 border-linkode-text rounded-linkode',
                'bg-transparent text-linkode-text',
                'font-mono font-bold uppercase text-sm',
                'transition-all duration-200',
                'hover:bg-linkode-text hover:text-white',
                'focus:outline-none focus:ring-2 focus:ring-linkode-teal'
              )}
            >
              {secondaryActionLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Export variants for testing
export { emptyStateVariants };
